import React, { useCallback, useEffect, useMemo, useState } from "react";

import { ColumnsType } from "antd/es/table";
import { Button, Input, message, Select, Space, Table } from "antd";
import { fetchSelectOptionsByContractNO } from "../../../../backendInteraction/ApiFetch";

import { depthValueValidator } from "../../../../utils/Validator";
import { BitData } from "../../../../types/BitData";
import { addBitRow, deleteBitByBitId, fetchBitsByPid, updateBitRow } from "../../../../backendInteraction/BitsApiFetch";

interface BitsTableProps {
    data: BitData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
    holeId: string;
    setHoleId: (value: string) => void;
}
const BitsTable: React.FC<BitsTableProps> = ({data, plodId, contractNo, isView, isAddNewRow, setIsAddNewRow, holeId, setHoleId}) => {
    const [bitsData, setBitsData] = useState<BitData []>([]);

    const [borOptions, setBorOptions] = useState<{value: string; label: string}[]>([]);
    const [sizeOptions, setSizeOptions] = useState<{value: string; label: string}[]>([]);
    const [typeOptions, setTypeOptions] = useState<{value: string; label: string}[]>([]);

    const [editingBitId, setEditingBitId] = useState<number | null>(null);
    const [editData, setEditData] = useState<BitData | null>(null);


    const loadOptions = useCallback(async () => {
        if (contractNo) {
            const bors = await fetchSelectOptionsByContractNO(contractNo, "BitOrReamer");
            const sizes = await fetchSelectOptionsByContractNO(contractNo, "BitSize");
            const types = await fetchSelectOptionsByContractNO(contractNo, "Manufacturer & Bit Type");
            setBorOptions(bors.map(bor => ({ value: bor, label: bor })));
            setSizeOptions(sizes.map(size => ({ value: size, label: size })));
            setTypeOptions(types.map(type => ({ value: type, label: type })));
        } else {
            setBorOptions([]);
            setSizeOptions([]);
            setTypeOptions([]);
        }
    }, [contractNo]);

    useEffect(() => {
        loadOptions();
    }, [loadOptions]);

    useEffect(() => {
        setBitsData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingBitId !== -1) {
            const len = bitsData.length;
            const newRow: BitData = {
                key: -1,
                bit_id: -1,
                hole_id: len > 0 ? bitsData[len - 1].hole_id || holeId : holeId,
                bit_or_reamer: "",
                serial_no: "",
                size: "",
                type: "",
                depth_from: len > 0 ? bitsData[len - 1].depth_to : "",
                depth_to: "",
                total_metres: "",
                pid: plodId ?? -1,
            };
            setBitsData(prevData => {
                const hasNewRow = prevData.some(item => item.bit_id === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            });

            setEditingBitId(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingBitId]);

    useEffect(() => {
        if (isView) {
            if (editingBitId === -1) {
                setBitsData(prevData => prevData.filter(item => item.bit_id !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingBitId(null);
            setEditData(null);
        }
    }, [isView]);

    const saveChanges = useCallback(async () => {
        console.log("Saving changes:", editData);

        if (!editData) return;

        const { depth_from, depth_to, hole_id } = editData;

        if(!depthValueValidator(depth_from, depth_to, false)) return;

        const updatedEditData = { ...editData, total_metres: !!depth_from&&!!depth_to ? parseFloat(depth_to) - parseFloat(depth_from) + '' : ''};
        setEditData(updatedEditData);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingBitId === -1) {
            const success = await addBitRow(updatedEditData, aid);
            if (success) {
                setBitsData(await fetchBitsByPid(plodId ?? -1, aid));
                message.success("New bits added successfully!");
            } else {
                message.error("Failed to add new bits.");
            }
        } else {
            const success = await updateBitRow(editingBitId ?? -1, updatedEditData);
            if (success) {
                setBitsData(await fetchBitsByPid(plodId ?? -1, aid));
                message.success("Bits updated successfully!");
            } else {
                message.error("Failed to update bits.");
            }
        }

        !(hole_id === holeId) && setHoleId(hole_id);

        if (editingBitId === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingBitId(null);
        setEditData(null);
    }, [editData, editingBitId, setIsAddNewRow]);

    const cancelEditing = useCallback((record: BitData) => {
        console.log("Cancel editing");
        setEditingBitId(null);
        setEditData(null);

        if (record.bit_id === -1) {
            setBitsData(prevData => prevData.filter(item => item.bit_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: BitData) => {
        console.log("Editing row:", record);

        if (editingBitId === -1) {
            setBitsData(prevData => prevData.filter(item => item.bit_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingBitId(prevId => {
            if (prevId !== null) cancelEditing({ bit_id: prevId } as BitData);
            return record.bit_id;
        });

        setEditData({ ...record });
    }, [editingBitId, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: BitData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const success = await deleteBitByBitId(record.bit_id);
        if (success) {
            setBitsData(await fetchBitsByPid(plodId ?? -1, aid));
            message.success("Bits deleted successfully!");
        } else {
            message.error("Failed to delete bits.");
        }
    }, []);

    const handleInputChange = useCallback((key: keyof BitData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);

    const columns: ColumnsType<BitData> = useMemo(() => [
        {
            title: 'HoleID',
            dataIndex: 'hole_id',
            key: 'hole_id',
            width: "10%",
            render: (_, record) =>
                editingBitId === record.bit_id ? (
                    <Input
                        value={editData?.hole_id}
                        onChange={(e) => handleInputChange("hole_id", e.target.value)}
                    />
                ) : (
                    record.hole_id
                ),
        },
        {
            title: 'Bit/Reamer',
            dataIndex: 'bit_or_reamer',
            key: 'bit_or_reamer',
            width: "8%",
            render: (_, record) =>
                editingBitId === record.bit_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={borOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.bit_or_reamer || ""}
                        onChange={(value) => handleInputChange("bit_or_reamer", value || "")}
                    />
                ) : (
                    record.bit_or_reamer
                ),
        },
        {
            title: 'SerialNo',
            dataIndex: 'serial_no',
            key: 'serial_no',
            width: "10%",
            render: (_, record) =>
                editingBitId === record.bit_id ? (
                    <Input
                        value={editData?.serial_no}
                        onChange={(e) => handleInputChange("serial_no", e.target.value)}
                    />
                ) : (
                    record.serial_no
                ),
        },
        {
            title: 'Size',
            dataIndex: 'size',
            key: 'size',
            width: "12%",
            render: (_, record) =>
                editingBitId === record.bit_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={sizeOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.size || ""}
                        onChange={(value) => handleInputChange("size", value || "")}
                    />
                ) : (
                    record.size
                ),
        },
        {
            title: 'Type',
            dataIndex: 'type',
            key: 'type',
            width: "22%",
            render: (_, record) =>
                editingBitId === record.bit_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={typeOptions}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.type || ""}
                        onChange={(value) => handleInputChange("type", value || "")}
                    />
                ) : (
                    record.type
                ),
        },
        {
            title: 'From',
            dataIndex: 'depth_from',
            key: 'depth_from',
            width: "8%",
            render: (_, record) =>
                editingBitId === record.bit_id ? (
                    <Input
                        value={editData?.depth_from}
                        onChange={(e) => handleInputChange("depth_from", e.target.value)}
                    />
                ) : (
                    record.depth_from
                ),
        },
        {
            title: 'To',
            dataIndex: 'depth_to',
            key: 'depth_to',
            width: "8%",
            render: (_, record) =>
                editingBitId === record.bit_id ? (
                    <Input
                        value={editData?.depth_to}
                        onChange={(e) => handleInputChange("depth_to", e.target.value)}
                    />
                ) : (
                    record.depth_to
                ),
        },
        {
            title: 'Total(m)',
            dataIndex: 'total_metres',
            key: 'total_metres',
            width: "8%",
            render: (_, record) =>( record.total_metres ),
        },
        ...(!isView
            ? [{
                title: 'Action',
                key: 'action',
                width: "14%",
                render: (_, record) => (
                    <Space size="small">
                        <Button color="geekblue" variant="text"
                                onClick={() => (editingBitId === record.bit_id ? saveChanges() : startEditing(record))}>
                            {editingBitId === record.bit_id ? "Save" : "Edit"}
                        </Button>
                        <Button color="geekblue" variant="text"
                                onClick={() => (editingBitId === record.bit_id ? cancelEditing(record) : deleteRow(record))}>
                            {editingBitId === record.bit_id ? "Cancel" : "Delete"}
                        </Button>
                    </Space>
                ),
            },
            ] as ColumnsType<BitData>
            : []),
    ], [isView, editingBitId, borOptions, sizeOptions, typeOptions, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<BitData>
            columns={columns}
            pagination={false}
            dataSource={bitsData}
        />
    );
};

export default BitsTable;